import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { makeStyles, tokens, Spinner, Text } from '@fluentui/react-components';
import { Sparkle24Filled, Dismiss24Regular } from '@fluentui/react-icons';
import { apiClient } from '../../../services/api/apiClient';
import { createSpeechPonyfillFactory } from '../../../utils/speechPonyfill';
import { createManualSubmitWebChatStore } from '../../../utils/webchatStore';
import { ROUTES } from '../../../config/routes';

const useStyles = makeStyles({
  fab: {
    position: 'fixed',
    bottom: '24px',
    right: '24px',
    width: '56px',
    height: '56px',
    minWidth: '56px',
    borderRadius: '50%',
    zIndex: 1000,
    boxShadow: tokens.shadow16,
    backgroundColor: tokens.colorBrandBackground,
    color: '#fff',
    border: 'none',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.2s, box-shadow 0.2s',
    ':hover': {
      transform: 'scale(1.1)',
      boxShadow: tokens.shadow28,
      backgroundColor: tokens.colorBrandBackgroundHover,
    },
  },
  popup: {
    position: 'fixed',
    bottom: '90px',
    right: '24px',
    width: '420px',
    height: 'calc(100vh - 140px)',
    maxHeight: '700px',
    borderRadius: '16px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow28,
    zIndex: 1000,
    flexDirection: 'column',
    overflow: 'hidden',
  },
  popupHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  closeBtn: {
    cursor: 'pointer',
    border: 'none',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '4px',
    borderRadius: '4px',
    color: tokens.colorNeutralForeground2,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground3,
    },
  },
  webchatContainer: {
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    height: '100%',
    '& table': {
      width: '100%',
      borderCollapse: 'collapse',
      marginTop: '8px',
      marginBottom: '8px',
    },
    '& table th, & table td': {
      border: '1px solid #d1d5db',
      padding: '6px 10px',
      textAlign: 'left',
      fontSize: '12px',
    },
    '& table th': {
      backgroundColor: '#f3f4f6',
      fontWeight: 600,
    },
  },
  centered: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '100%',
  },
});

export const ChatBubble = () => {
  const styles = useStyles();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const webchatRef = useRef<HTMLDivElement>(null);
  const initDone = useRef(false);

  const isAssistantPage = location.pathname === ROUTES.ASSISTANT;

  const initWebChat = useCallback(async () => {
    if (initDone.current) return;
    initDone.current = true;
    try {
      const { token } = await apiClient.post<{ token: string; conversationId?: string }>(
        '/api/assistant/directline-token',
        {}
      );
      if (!window.WebChat || !webchatRef.current) {
        setError('WebChat not available');
        return;
      }
      const directLine = window.WebChat.createDirectLine({
        token,
        domain: 'https://europe.directline.botframework.com/v3/directline',
      });
      const webSpeechPonyfillFactory = await createSpeechPonyfillFactory();
      const store = createManualSubmitWebChatStore();
      const renderOpts = (withSpeech: boolean) => ({
        directLine,
        ...(store ? { store } : {}),
        ...(withSpeech && webSpeechPonyfillFactory ? { webSpeechPonyfillFactory } : {}),
        locale: 'fr-FR',
        styleOptions: {
          hideUploadButton: true,
          rootHeight: '100%',
          rootWidth: '100%',
          bubbleMaxWidth: 380,
          bubbleBackground: tokens.colorNeutralBackground3,
          bubbleFromUserBackground: tokens.colorBrandBackground,
          bubbleFromUserTextColor: tokens.colorNeutralForegroundOnBrand,
        },
      });
      try {
        window.WebChat.renderWebChat(renderOpts(true), webchatRef.current);
      } catch {
        window.WebChat.renderWebChat(renderOpts(false), webchatRef.current);
      }
      setLoaded(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Connection failed');
    }
  }, []);

  useEffect(() => {
    if (open && !initDone.current) {
      void initWebChat();
    }
  }, [open, initWebChat]);

  return (
    <>
      {/* FAB button — hidden when popup is open or on assistant page */}
      <button
        className={styles.fab}
        onClick={() => setOpen(true)}
        title="AI Assistant"
        style={{ display: open || isAssistantPage ? 'none' : 'flex' }}
      >
        <Sparkle24Filled />
      </button>

      {/* Popup — always mounted, toggled via display, hidden on assistant page */}
      <div
        className={styles.popup}
        style={{ display: open && !isAssistantPage ? 'flex' : 'none' }}
      >
        <div className={styles.popupHeader}>
          <Text weight="semibold" size={400}>AI Assistant</Text>
          <button
            className={styles.closeBtn}
            onClick={() => setOpen(false)}
            title="Close"
          >
            <Dismiss24Regular />
          </button>
        </div>
        <div style={{ flex: 1, minHeight: 0, position: 'relative' }}>
          {/* Overlay for loading/error — separate from WebChat DOM */}
          {!loaded && !error && (
            <div className={styles.centered} style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
              <Spinner size="small" label="Connecting..." />
            </div>
          )}
          {error && (
            <div className={styles.centered} style={{ position: 'absolute', inset: 0, zIndex: 1 }}>
              <Text size={200}>{error}</Text>
            </div>
          )}
          {/* WebChat render target — React must NEVER put children here */}
          <div ref={webchatRef} className={styles.webchatContainer} />
        </div>
      </div>
    </>
  );
};
